import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useVideoContext } from './VideoContext'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import useDebouncedCallback from '../../utils/debouncer';

dayjs.extend(duration);

export default function SeekBar2({
  // Constants to setup the time bar
  pixelsPerSecond = 8,
  majorTickSecs = 5,
  minorTicksPerMajor = 5,
  labelEveryMajors = 3,

  // Not going to set a hard stop on past minutes
  maxPastMinutes = null,
  futureMinutes = 0,

  // callbacks
  onSeek,                // fires continuously while scrubbing
  onSeekCommit,          // fires when scrubbing ends
  unix_timestamp_s       // nested parameter
}) {
  const {
    setMasterPlaybackStart,
    masterIsLive,
    goLiveAll,
    playingRef,
    scrollWheelStart,
    requestSeek,
    windowStartMs,
    leaderVideoCurrentTime,
    masterPlaybackModeRef
  } = useVideoContext();
  let { state } = useLocation()

  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [userTime, setUserTime] = useState(() => Date.now());

  const usTimeZoneMap = {
    EST: "America/New_York",      // Eastern Standard Time
    EDT: "America/New_York",      // Eastern Daylight Time
    CST: "America/Chicago",       // Central Standard Time
    CDT: "America/Chicago",       // Central Daylight Time
    MST: "America/Denver",        // Mountain Standard Time
    MDT: "America/Denver",        // Mountain Daylight Time
    PST: "America/Los_Angeles",   // Pacific Standard Time
    PDT: "America/Los_Angeles",   // Pacific Daylight Time
    AKST: "America/Anchorage",    // Alaska Standard Time
    AKDT: "America/Anchorage",    // Alaska Daylight Time
    HST: "Pacific/Honolulu"       // Hawaii Standard Time (no DST)
  };

  // Ref for tracking the delta between client time, and actual playback time. Might not be needed?
  const userOffsetRef = useRef(0); // userTime - currentTime

  // Supports user dragging the time bar
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startUserTime = useRef(0);

  // This is to dynamically build out the seekbar. Odd approach but it works, dont feel like messing with it
  const barRef = useRef(null);
  const [barWidth, setBarWidth] = useState(0);

  // Derived constants
  const majorTickMs = majorTickSecs * 1000;
  const minorStepMs = majorTickMs / (minorTicksPerMajor || 1);
  useEffect(()=> {
    if (unix_timestamp_s != null){
      let initial_unix_timestamp = dayjs(unix_timestamp_s)
      seekTo(initial_unix_timestamp)
    }
  }, unix_timestamp_s)

  // MODIFIED: Update currentTime and userTime to track video position
  useEffect(() => {
    let anim = 0;
    const tick = () => {
      const now = Date.now();
      setCurrentTime(now);

      if (!isDragging.current) {
        if (playingRef.current) {
          // In playback mode with video position available, sync to actual video
          if (masterPlaybackModeRef.current === "playback" && windowStartMs != null && leaderVideoCurrentTime != null) {
            const videoAbsTime = windowStartMs + leaderVideoCurrentTime * 1000;
            setUserTime(videoAbsTime)
            userOffsetRef.current = videoAbsTime - now;
          } else {
            // Live mode or no video position: advance with real time
            setUserTime(now + userOffsetRef.current);
          }
        } else {
          // Paused: just rebaseline offset
          userOffsetRef.current = userTime - now;
        }
      }
      anim = requestAnimationFrame(tick);
    };
    anim = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(anim);
  }, [playingRef, masterPlaybackModeRef.current, windowStartMs, leaderVideoCurrentTime, userTime]);

  // Keep offset in sync
  useEffect(() => {
    userOffsetRef.current = userTime - currentTime;
  }, [userTime, currentTime]);

  // Measure bar when rendering, probably could use CSS instead? 
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setBarWidth(e.contentRect.width);
    });
    if (barRef.current) ro.observe(barRef.current);
    return () => ro.disconnect();
  }, []);

  // mark when we are the source of the masterPlaybackStart change
  const localUpdateRef = useRef(false);
  const prevStartMsRef = useRef(null);
  // call this where you update masterPlaybackStart so the effect can ignore the echo
  const setMasterPlaybackStartLocal = (ms) => {
    localUpdateRef.current = true;
    setMasterPlaybackStart(Math.round(ms / 1000));
  };

  const debouncedCommit = useDebouncedCallback((ms) => {
    requestSeek(ms);
  }, 1000);


  useEffect(() => {
    if (!scrollWheelStart) return;
    const { time: newStartMs } = scrollWheelStart; // unwrap
    if (newStartMs == null) return;
    // Ignore the immediate echo of our own local set (wheel/drag)
    if (localUpdateRef.current) {
      localUpdateRef.current = false;
      prevStartMsRef.current = newStartMs;
      return;
    }
    // SNAP to new time
    setUserTime(newStartMs);
  
    // Rebaseline offset so rAF flow feels right
    userOffsetRef.current = newStartMs - Date.now();
  
    // Optional focus so wheel applies here
    barRef.current?.focus();
  
    // Always update reference (but don't block repeat values)
    prevStartMsRef.current = newStartMs;
  }, [scrollWheelStart]);

  // --- Limits & helpers ---
  const clampUserTime = useCallback((t) => {
    const min = maxPastMinutes == null ? -Infinity : currentTime - maxPastMinutes * 60_000;
    const max = Math.min(currentTime + futureMinutes * 60_000, currentTime); // never beyond NOW
    return Math.max(min, Math.min(t, max));
  }, [currentTime, maxPastMinutes, futureMinutes]);

  const seekTo = useCallback((t, { commit = false } = {}) => {
    const next = clampUserTime(t);
    setUserTime(next);
    onSeek?.(next);

    if (commit) {
      // flush immediately, cancel pending debounce
      debouncedCommit.cancel();
      setMasterPlaybackStartLocal(next);
      onSeekCommit?.(next);
    } else {
      // debounce API update
      debouncedCommit(next);
    }
  }, [clampUserTime, onSeek, onSeekCommit, debouncedCommit]);

  const goLive = useCallback(() => {
    seekTo(currentTime, { commit: true });
    goLiveAll()
  }, [currentTime, seekTo, goLiveAll]);

  // --- Pointer scrubbing ---
  const onPointerDown = (e) => {
    if (!barRef.current) return;
    barRef.current.setPointerCapture(e.pointerId);
    isDragging.current = true;
    startX.current = e.clientX;
    startUserTime.current = userTime;
  };

  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    const deltaX = e.clientX - startX.current;
    const deltaMs = -(deltaX / pixelsPerSecond) * 1000; // right drag -> later time
    seekTo(startUserTime.current + deltaMs);
  };

  const onPointerUp = (e) => {
    if (!barRef.current) return;
    try { barRef.current.releasePointerCapture(e.pointerId); } catch (_) { }
    isDragging.current = false;
    requestSeek(userTime)
    onSeekCommit?.(userTime);
  };

  // --- Selection offset relative to NOW: drives where ticks appear without moving center chip ---
  const selectionOffsetMs = userTime - currentTime;

  // --- Build ticks to cover the visible area around the CENTER (NOW) ---
  const secondsVisible = Math.max(1, Math.ceil(barWidth / pixelsPerSecond));
  const halfSpanMs = (secondsVisible / 2) * 1000;
  const startMs = currentTime + selectionOffsetMs - halfSpanMs - majorTickMs; // pad both sides
  const endMs = currentTime + selectionOffsetMs + halfSpanMs + majorTickMs;

  // Align to absolute major grid so labels/ticks are stable
  const firstAlignedMajor = Math.floor(startMs / majorTickMs) * majorTickMs;

  const majors = [];
  for (let t = firstAlignedMajor; t <= endMs; t += majorTickMs) majors.push(t);

  const showLabelFor = (tMajor) => {
    const idx = Math.floor(tMajor / majorTickMs);
    const mod = ((idx % labelEveryMajors) + labelEveryMajors) % labelEveryMajors;
    return mod === 0;
  };

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 16, boxSizing: "border-box", userSelect: "none" }}>
      {/* Controls header with persistent Go Live */}
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <button
          onClick={goLiveAll}
          title="Go Live (L or End)"
          style={{
            padding: "4px 10px",
            fontSize: 12,
            borderRadius: 8,
            border: "none",
            background: masterIsLive ? "#10B981" : "#7C3AED",
            color: masterIsLive ? "black" : "white",
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          }}
          disabled={masterIsLive}
        >
          {masterIsLive ? "LIVE" : "Go Live"}
        </button>
      </div>

      {/* Seekbar itself */}
      <div
        ref={barRef}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          position: "relative",
          width: "100%",
          height: 70,
          background: "#E5E7EB",
          borderRadius: 10,
          overflow: "hidden",
          touchAction: "none",
          cursor: isDragging.current ? "grabbing" : "grab",
          outline: "none",
        }}
      >
        {/* Tick strip (anchored at center NOW). We do not translate the whole strip */}
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "50%", willChange: "transform" }}>
          {majors.map((tMajor) => {
            const xMajor = ((tMajor - (currentTime + selectionOffsetMs)) / 1000) * pixelsPerSecond;
            const showLabel = showLabelFor(tMajor);
            return (
              <div key={tMajor} style={{ position: "absolute", left: xMajor }}>
                {/* Major tick */}
                <div style={{ width: 2, height: 28, background: "#6B7280", position: "absolute", top: 12, transform: "translateX(-1px)" }} />
                {/* Minor ticks between tMajor and next major */}
                {Array.from({ length: minorTicksPerMajor - 1 }).map((_, i) => {
                  const tMinor = tMajor + (i + 1) * minorStepMs;
                  if (tMinor > endMs) return null;
                  const xMinor = ((tMinor - (currentTime + selectionOffsetMs)) / 1000) * pixelsPerSecond;
                  return (
                    <div key={`${tMajor}-m-${i}`} style={{ position: "absolute", top: 18, left: xMinor - xMajor, width: 1, height: 16, background: "#9CA3AF" }} />
                  );
                })}
                {/* Absolute, stable label */}
                
                {showLabel && (
                  <div style={{ position: "absolute", top: 46, left: 0, transform: "translateX(-50%)", fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                    {new Date(tMajor).toLocaleTimeString("en-US", { timeZone: usTimeZoneMap[state?.trailer_time_zone], hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Edge fade masks so time appears to come in from edges */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 80, background: "linear-gradient(to right, rgba(229,231,235,1), rgba(229,231,235,0))" }} />
          <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: 80, background: "linear-gradient(to left, rgba(229,231,235,1), rgba(229,231,235,0))" }} />
        </div>

        {/* Center NOW line */}
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: 2, background: "#111827", transform: "translateX(-1px)", pointerEvents: "none" }} />

        {/* Center-top playhead label (fixed at center) */}
        <div style={{ position: "absolute", top: 2, left: "50%", transform: "translateX(-50%)", fontSize: 12, background: "rgba(17,24,39,0.85)", color: "white", padding: "2px 8px", borderRadius: 8, pointerEvents: "none", display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', whiteSpace: 'nowrap', alignItems: 'center' }}>
          {new Date(userTime).toLocaleTimeString("en-US", {  timeZone: usTimeZoneMap[state?.trailer_time_zone], month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })} {state?.trailer_time_zone}
        </div>
      </div>
    </div>
  );
}